import * as S from "@effect/schema/Schema";
import { secretbox } from "@noble/ciphers/salsa";
import { concatBytes } from "@noble/ciphers/utils";
import { hmac } from "@noble/hashes/hmac";
import { sha512 } from "@noble/hashes/sha512";
import { randomBytes } from "@noble/hashes/utils";
import * as bip39 from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
/**
 * Mnemonic is a password generated by Evolu in BIP39 format.
 *
 * A mnemonic, also known as a "seed phrase," is a set of 12 words in a specific
 * order chosen from a predefined list. The purpose of the BIP39 mnemonic is to
 * provide a human-readable way of storing a private key.
 */
export const createMnemonic = () => bip39.generateMnemonic(wordlist, 128);
/** Parse a string to {@link Mnemonic}. */
export const parseMnemonic = (mnemonic) => {
    const mnemonicTrimmed = mnemonic.trim();
    return bip39.validateMnemonic(mnemonicTrimmed, wordlist)
        ? Effect.succeed(mnemonicTrimmed)
        : Effect.fail({
            _tag: "InvalidMnemonicError",
        });
};
export const mnemonicToSeed = (mnemonic) => bip39.mnemonicToSeedSync(mnemonic);
export class NanoIdGenerator extends Context.Tag("NanoIdGenerator")() {
}
export const createNanoIdGeneratorLive = (customAlphabet, nanoid) => {
    const nanoidForNodeId = customAlphabet("0123456789abcdef", 16);
    return Layer.succeed(NanoIdGenerator, NanoIdGenerator.of({
        nanoid: Effect.sync(() => nanoid()),
        nodeId: Effect.sync(() => nanoidForNodeId()),
        rowId: Effect.sync(() => nanoid()),
    }));
};
export const NodeId = S.String.pipe(S.pattern(/^[\w-]{16}$/), S.brand("NodeId"));
// SLIP-21 implementation
// https://github.com/satoshilabs/slips/blob/master/slip-0021.md
export const slip21Derive = (seed, path) => Effect.sync(() => {
    let m = hmac(sha512, "Symmetric key seed", seed);
    for (let i = 0; i < path.length; i++) {
        const p = new TextEncoder().encode(path[i]);
        const e = new Uint8Array(p.byteLength + 1);
        e[0] = 0;
        e.set(p, 1);
        m = hmac(sha512, m.slice(0, 32), e);
    }
    return m.slice(32, 64);
});
export class SecretBox extends Context.Tag("SecretBox")() {
}
SecretBox.Live = Layer.succeed(SecretBox, SecretBox.of({
    seal: (key, plaintext) => Effect.sync(() => {
        const nonce = randomBytes(24);
        const ciphertext = secretbox(key, nonce).seal(plaintext);
        return concatBytes(nonce, ciphertext);
    }),
    open: (key, ciphertext) => Effect.sync(() => {
        const nonce = ciphertext.subarray(0, 24);
        const ciphertextWithoutNonce = ciphertext.subarray(24);
        return secretbox(key, nonce).open(ciphertextWithoutNonce);
    }),
}));
